import axiosInstance from '../utils/axios';

/**
 * 대시보드 서비스
 * 
 * 메인 대시보드에 필요한 통계 및 요약 데이터를 조회합니다.
 */
const dashboardService = {
  /**
   * 대시보드 전체 데이터 조회
   * 여러 API를 병렬로 호출하여 대시보드에 필요한 모든 데이터를 수집합니다.
   * @returns {Promise<Object>} 대시보드 데이터
   */
  async getDashboardData() {
    try {
      const [
        sessionStats,
        systemAlerts,
        notices,
        auditTimeline,
        auditCategory,
        securityEvents,
        alertOverview,
      ] = await Promise.allSettled([
        this.getSessionStatistics(),
        this.getEffectiveAlerts(),
        this.getRecentNotices(),
        this.getAuditTimeline(),
        this.getAuditCategoryStats(),
        this.getRecentSecurityEvents(),
        this.getAlertDashboardOverview(),
      ]);

      const result = {
        success: true,
        data: {
          sessionStats:
            sessionStats.status === "fulfilled" ? sessionStats.value : null,
          systemAlerts:
            systemAlerts.status === "fulfilled" ? systemAlerts.value : [],
          notices: notices.status === "fulfilled" ? notices.value : [],
          auditTimeline:
            auditTimeline.status === "fulfilled" ? auditTimeline.value : null,
          auditCategory:
            auditCategory.status === "fulfilled" ? auditCategory.value : null,
          securityEvents:
            securityEvents.status === "fulfilled" ? securityEvents.value : [],
          alertOverview:
            alertOverview.status === "fulfilled" ? alertOverview.value : null,
        },
      };

      return result;
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
      return {
        success: false,
        message: '대시보드 데이터를 불러오는데 실패했습니다.'
      };
    }
  },

  /**
   * 세션 통계 조회
   * @returns {Promise<Object>} 세션 통계
   */
  async getSessionStatistics() {
    try {
      const response = await axiosInstance.get('/sessions/statistics');
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('세션 통계 조회 실패:', error);
      return null;
    }
  },

  /**
   * 유효한 시스템 알림 조회
   * @returns {Promise<Array>} 시스템 알림 목록
   */
  async getEffectiveAlerts() {
    try {
      const response = await axiosInstance.get('/system-alerts/effective/admin');
      if (response.data.success) {
        // alerts 배열이 data.alerts로 반환됨
        return response.data.data?.alerts || response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error('시스템 알림 조회 실패:', error);
      return [];
    }
  },

  /**
   * 최근 공지사항 조회
   * @param {number} limit 조회 개수
   * @returns {Promise<Array>} 공지사항 목록
   */
  async getRecentNotices(limit = 5) {
    try {
      const response = await axiosInstance.get('/notice/list', {
        params: { pageSize: limit, page: 1 }
      });
      if (response.data.success) {
        // notices 배열이 data.notices 또는 data.list로 반환될 수 있음
        const data = response.data.data;
        return data?.notices || data?.list || (Array.isArray(data) ? data : []);
      }
      return [];
    } catch (error) {
      console.error('공지사항 조회 실패:', error);
      return [];
    }
  },

  /**
   * 감사로그 시간대별 통계 조회
   * @param {number} days 조회 기간 (일)
   * @returns {Promise<Object>} 시간대별 통계
   */
  async getAuditTimeline(days = 7) {
    try {
      const response = await axiosInstance.get('/audit/stats/timeline', {
        params: { interval: 'day', days }
      });
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('감사로그 타임라인 조회 실패:', error);
      return null;
    }
  },

  /**
   * 감사로그 카테고리별 통계 조회
   * @returns {Promise<Object>} 카테고리별 통계
   */
  async getAuditCategoryStats() {
    try {
      const response = await axiosInstance.get('/audit/stats/category');
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('감사로그 카테고리 통계 조회 실패:', error);
      return null;
    }
  },

  /**
   * 최근 보안 이벤트 조회
   * @param {number} minutes 최근 N분
   * @returns {Promise<Array>} 보안 이벤트 목록
   */
  async getRecentSecurityEvents(minutes = 60) {
    try {
      const response = await axiosInstance.get('/audit/events/realtime', {
        params: { minutes }
      });
      if (response.data.success) {
        // events 배열이 직접 반환되거나 data.events로 반환될 수 있음
        const data = response.data.data;
        if (Array.isArray(data)) {
          return data;
        }
        return data?.events || [];
      }
      return [];
    } catch (error) {
      console.error('보안 이벤트 조회 실패:', error);
      return [];
    }
  },

  /**
   * 사용자 통계 조회 (총 사용자 수)
   * @returns {Promise<Object>} 사용자 통계
   */
  async getUserStatistics() {
    try {
      const response = await axiosInstance.get('/user/list', {
        params: { page: 1, pageSize: 1, state: 1 }
      });
      if (response.data.success) {
        // pagination.totalCount로 반환됨
        return {
          totalUsers:
            response.data.data?.pagination?.totalCount ||
            response.data.data?.totalCount ||
            0,
        };
      }
      return { totalUsers: 0 };
    } catch (error) {
      console.error('사용자 통계 조회 실패:', error);
      return { totalUsers: 0 };
    }
  },

  /**
   * 알림 대시보드 개요 조회 (알림 채널 대시보드와 동일한 API)
   * @param {string} timeRange 시간 범위 (24h, 7d, 30d)
   * @returns {Promise<Object>} 알림 대시보드 개요
   */
  async getAlertDashboardOverview(timeRange = '24h') {
    try {
      const response = await axiosInstance.get('/alert-dashboard/overview', {
        params: { time_range: timeRange }
      });
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('알림 대시보드 개요 조회 실패:', error);
      return null;
    }
  }
};

export default dashboardService;
